/**
 * KaiLux — Management Accounts
 * Route: /management-accounts
 *
 * Workflow (mirrors the professional rigour of the AFS flow):
 *   Step 1 — Select client
 *   Step 2 — Set engagement details (period, preparer, distribution basis)
 *   Step 3 — Enter figures (Income Statement + Balance Sheet, with live balance check)
 *   Step 4 — Review & generate → PDF download + AI commentary preview
 *   History tab — per-client pack history with re-download
 */

import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getClients, getFirm, generateManagementAccounts, getManagementAccountsHistory, redownloadManagementAccounts, redownloadManagementAccountsWord, extractManagementAccountsDocument, getOrCreateMAJob, saveMAJobProgress, resumeMAJobProgress, listMAJobDrafts, getMASourceDocuments } from "../api/kailux";
import { pointsToBilling } from '../utils/lockMessage'

import Icon from '../components/Icon'
import { T, alpha } from '../theme/theme'
// ── Shared styles ─────────────────────────────────────────────────────────────
const inp = {
  padding:'8px 12px', background:T.s1, border:`1px solid ${T.bd}`,
  borderRadius:'8px', color:T.tx, fontSize:'14px', outline:'none',
  fontFamily:"'Outfit',sans-serif",
};

const card = {
  background:T.s2, border:`1px solid ${T.bd}`, borderRadius:'12px', padding:'20px',
};

// ── Period helpers ─────────────────────────────────────────────────────────────
const MONTHS = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];

function buildPeriodOptions(count = 24) {
  const opts = [];
  const now = new Date();
  for (let i = 0; i < count; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`;
    const label = `${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
    opts.push({ value, label });
  }
  return opts;
}
const PERIOD_OPTS = buildPeriodOptions();

export function periodLabel(val) {
  if (!val) return "";
  const opt = PERIOD_OPTS.find(o => o.value === val);
  if (opt) return opt.label;
  // fallback parse
  const [y, m] = val.split("-");
  return `${MONTHS[parseInt(m,10)-1]} ${y}`;
}

// ── Figure field definitions ───────────────────────────────────────────────────
const IS_FIELDS = [
  { key:"revenue",        label:"Revenue" },
  { key:"cos",            label:"Cost of Sales" },
  { key:"other_income",   label:"Other Income" },
  { key:"employee_costs", label:"Employee Costs" },
  { key:"depreciation",   label:"Depreciation & Amortisation" },
  { key:"admin_expenses", label:"Administrative Expenses" },
  { key:"other_expenses", label:"Other Operating Expenses" },
  { key:"finance_costs",  label:"Finance Costs" },
  { key:"tax_expense",    label:"Tax Expense" },
];

const BS_ASSET_FIELDS = [
  { key:"cash",                 label:"Cash and Cash Equivalents" },
  { key:"receivables",          label:"Trade and Other Receivables" },
  { key:"inventories",          label:"Inventories" },
  { key:"other_current_assets", label:"Other Current Assets" },
  { key:"ppe",                  label:"Property, Plant and Equipment (net)" },
  { key:"intangibles",          label:"Intangible Assets" },
  { key:"investments",          label:"Investments" },
];

const BS_LE_FIELDS = [
  { key:"trade_payables",                label:"Trade and Other Payables" },
  { key:"short_term_borrowings",         label:"Short-term Borrowings" },
  { key:"tax_payable",                   label:"Tax Payable" },
  { key:"other_current_liabilities",     label:"Other Current Liabilities" },
  { key:"long_term_borrowings",          label:"Long-term Borrowings" },
  { key:"other_non_current_liabilities", label:"Other Non-current Liabilities" },
  { key:"share_capital",                 label:"Share Capital" },
  { key:"retained_earnings",             label:"Retained Earnings" },
];

const ALL_FIELDS = [...IS_FIELDS, ...BS_ASSET_FIELDS, ...BS_LE_FIELDS];
const EMPTY_FIGURES = Object.fromEntries(ALL_FIELDS.map(f => [f.key, ""]));

// ── Source document definitions ────────────────────────────────────────────
// Each unlocks specific figure fields, mirroring the AFS Additional Documents
// pattern — real evidence behind a figure, not a guess.
const SOURCE_DOCUMENT_TYPES = [
  {
    type: "trial_balance", title: "Trial Balance", required: false, recommended: true,
    unlocks: "Nearly every Income Statement and Balance Sheet figure, on a full accrual basis",
    description: "The strongest option — maps every account using the same AI engine AFS uses, giving you a real accrual-basis set of figures instead of a partial cash-basis picture. Accounts it can't confidently classify are flagged for manual review rather than guessed at.",
    accepts: ".xlsx,.xls,.csv",
  },
  {
    type: "bank_statement", title: "Bank Statement", required: false,
    unlocks: "Revenue, Cost of Sales, Employee Costs, Admin & Other Expenses, Finance Costs, Other Income",
    description: "Categorises every transaction and totals them into the Income Statement. Cash-basis only — doesn't cover PPE, debtors/creditors ageing, or non-cash items. A trial balance (above) gives a more complete, accrual-basis picture if you have one.",
    accepts: ".pdf,.xlsx,.xls,.csv",
  },
  {
    type: "fixed_asset_register", title: "Fixed Asset Register", required: false,
    unlocks: "PPE (net book value), Depreciation charge",
    description: "Replaces an estimated PPE figure with the real net book value and current-year depreciation from your asset register.",
    accepts: ".xlsx,.xls,.csv,.pdf",
  },
  {
    type: "debtors_age_analysis", title: "Debtors Age Analysis", required: false,
    unlocks: "Trade Receivables",
    description: "Real receivables balance (net of any allowance) instead of an estimate, plus a concentration-risk flag if one debtor dominates the book.",
    accepts: ".xlsx,.xls,.csv,.pdf",
  },
  {
    type: "creditors_age_analysis", title: "Creditors Age Analysis", required: false,
    unlocks: "Trade Payables",
    description: "Real payables balance from your creditors ageing report.",
    accepts: ".xlsx,.xls,.csv,.pdf",
  },
  {
    type: "loan_agreement", title: "Loan Agreement / Amortisation Schedule", required: false,
    unlocks: "Short-term & Long-term Borrowings",
    description: "Splits total borrowings into current and non-current portions per the actual repayment schedule. Interest still needs the bank statement or manual entry.",
    accepts: ".pdf,.xlsx,.xls,.csv",
  },
  {
    type: "prior_year_afs", title: "Prior Year Signed AFS", required: false,
    unlocks: "Prior period figures (all fields)",
    description: "Populates the entire PRIOR PERIOD column from last year's signed AFS, confirming opening-balance continuity instead of retyping it.",
    accepts: ".pdf",
  },
];

const DOC_TYPE_LABELS = Object.fromEntries(SOURCE_DOCUMENT_TYPES.map(d => [d.type, d.title]));

// ── Derived totals ─────────────────────────────────────────────────────────────
export function computeTotals(f) {
  const n = k => parseFloat(f[k]) || 0;
  const rev  = n("revenue");
  const cos  = n("cos");
  const gp   = rev - cos;
  const oi   = n("other_income");
  const opex = n("employee_costs") + n("depreciation") + n("admin_expenses") + n("other_expenses");
  const fin  = n("finance_costs");
  const tax  = n("tax_expense");
  const net  = gp + oi - opex - fin - tax;

  const assets = n("cash") + n("receivables") + n("inventories") +
                 n("other_current_assets") + n("ppe") + n("intangibles") + n("investments");
  const liab   = n("trade_payables") + n("short_term_borrowings") + n("tax_payable") +
                 n("other_current_liabilities") + n("long_term_borrowings") + n("other_non_current_liabilities");
  const eq     = n("share_capital") + n("retained_earnings");
  const bsDiff = Math.abs(assets - liab - eq);

  return { gp, net, rev, assets, liab, eq, bsDiff };
}

export function fmt(v) {
  const n = parseFloat(v) || 0;
  if (n === 0) return "R 0";
  const s = Math.abs(n).toLocaleString("en-ZA", { minimumFractionDigits:0 });
  return n < 0 ? `(R ${s})` : `R ${s}`;
}

// ── Steps ──────────────────────────────────────────────────────────────────────
const STEPS = [
  { id:1, label:"Client" },
  { id:2, label:"Engagement" },
  { id:3, label:"Source Docs" },
  { id:4, label:"Figures" },
  { id:5, label:"Review & Generate" },
];

// ── Sub-components ─────────────────────────────────────────────────────────────
function StepBar({ current }) {
  return (
    <div style={{ display:"flex", alignItems:"center", gap:"0", marginBottom:"28px" }}>
      {STEPS.map((s, i) => {
        const done    = s.id < current;
        const active  = s.id === current;
        const future  = s.id > current;
        return (
          <div key={s.id} style={{ display:"flex", alignItems:"center", flex: i < STEPS.length-1 ? "1" : "0" }}>
            <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:"5px" }}>
              <div style={{
                width:"28px", height:"28px", borderRadius:"50%",
                display:"flex", alignItems:"center", justifyContent:"center",
                fontSize:"12px", fontWeight:700,
                background: done ? T.gn : active ? T.or : T.s3,
                color: (done || active) ? T.onpri : T.lt,
                border: future ? `2px solid ${T.bd}` : "none",
                transition:"all 0.2s",
              }}>
                {done ? <Icon name="check" /> : s.id}
              </div>
              <span style={{ fontSize:"11px", fontWeight:600, whiteSpace:"nowrap",
                color: active ? T.tx : done ? T.gn : T.lt }}>
                {s.label}
              </span>
            </div>
            {i < STEPS.length-1 && (
              <div style={{ flex:1, height:"2px", margin:"0 6px 18px",
                background: done ? T.gn : T.bd, transition:"background 0.3s" }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

function SectionLabel({ children }) {
  return (
    <div style={{ marginTop:"20px", marginBottom:"10px" }}>
      <p style={{ margin:0, fontSize:"10px", fontWeight:700, textTransform:"uppercase",
        letterSpacing:"0.10em", color:T.lt }}>{children}</p>
      <div style={{ height:"1px", background:T.bd, marginTop:"6px" }} />
    </div>
  );
}

function SourceBadge({ source }) {
  if (!source || source === "manual") return null;
  return (
    <span title={`From: ${DOC_TYPE_LABELS[source] || source}`} style={{
      fontSize:"9px", fontWeight:700, padding:"2px 6px", borderRadius:"5px",
      background:T.gnd, color:T.gn, whiteSpace:"nowrap", flexShrink:0 }}>
      <Icon name="check" /> sourced
    </span>
  );
}

function FigureRow({ label, value, onChange, disabled, highlight, source }) {
  return (
    <div style={{
      display:"flex", alignItems:"center", gap:"12px",
      padding:"5px 8px", borderRadius:"6px",
      background: highlight ? T.orb : "transparent",
    }}>
      <span style={{ flex:1, fontSize:"13px", color: highlight ? T.tx : T.mt,
        fontWeight: highlight ? 700 : 400, display:"flex", alignItems:"center", gap:"8px" }}>
        {label}
        <SourceBadge source={source} />
      </span>
      {onChange ? (
        <div style={{ position:"relative", width:"150px", flexShrink:0 }}>
          <span style={{ position:"absolute", left:"9px", top:"50%", transform:"translateY(-50%)",
            color:T.lt, fontSize:"12px", pointerEvents:"none" }}>R</span>
          <input type="number" step="any" value={value}
            onChange={e => onChange(e.target.value)}
            disabled={disabled} placeholder="0"
            style={{ width:"100%", boxSizing:"border-box",
              paddingLeft:"22px", paddingRight:"8px", paddingTop:"5px", paddingBottom:"5px",
              textAlign:"right", fontSize:"13px",
              background: disabled ? T.bg : T.s1,
              border:`1px solid ${T.bd}`, borderRadius:"7px",
              color: disabled ? T.lt : T.tx,
              outline:"none", fontFamily:"'Outfit',sans-serif" }} />
        </div>
      ) : (
        <span style={{ width:"150px", textAlign:"right", fontSize:"13px",
          fontWeight:700, color: highlight ? T.or : T.tx }}>
          {value}
        </span>
      )}
    </div>
  );
}

function Spinner() {
  return (
    <svg fill="none" viewBox="0 0 24 24"
      style={{ width:16, height:16, animation:"spin 1s linear infinite" }}>
      <circle style={{opacity:0.25}} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
      <path style={{opacity:0.75}} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
    </svg>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function ManagementAccounts() {
  const navigate   = useNavigate();
  const { firm: authFirm } = useAuth();

  // App state
  const [firm,           setFirm]           = useState(null);
  const [clients,        setClients]        = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [loadError,      setLoadError]      = useState(null);

  // Step state
  const [step,           setStep]           = useState(1);
  const [activeTab,      setActiveTab]      = useState("generate"); // generate | history

  // Step 1 — client
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientSearch,   setClientSearch]   = useState("");

  // Step 2 — engagement details
  const [period,         setPeriod]         = useState(PERIOD_OPTS[1]?.value || "");
  const [priorPeriod,    setPriorPeriod]    = useState(PERIOD_OPTS[2]?.value || "");
  const [usePrior,       setUsePrior]       = useState(false);
  const [preparedBy,     setPreparedBy]     = useState("");
  const [externalUse,    setExternalUse]    = useState(false);

  // Step 3 — source documents
  // docState[type] = { status: idle|uploading|done|error, fileName, notes, needsReview, transactionCount, error }
  const [docState,       setDocState]       = useState({});
  const [figureSources,  setFigureSources]  = useState({});      // {fieldKey: docType|"manual"}
  const [priorFigureSources, setPriorFigureSources] = useState({});

  // Step 4 — figures
  const [figures,        setFigures]        = useState(EMPTY_FIGURES);
  const [priorFigures,   setPriorFigures]   = useState(EMPTY_FIGURES);

  // Step 5 — generate
  const [generating,     setGenerating]     = useState(false);
  const [genError,       setGenError]       = useState(null);
  const [result,         setResult]         = useState(null);

  // Draft persistence / resume (Sep 2026) — mirrors the AFS bank-statement
  // flow's draft-job + autosave + resume system. maJobId is created once
  // client+period are known (end of step 2) and is what every subsequent
  // autosave and the final generate call attach to.
  const [maJobId,        setMaJobId]        = useState(null);
  const [availableDraft, setAvailableDraft] = useState(null);   // {id, period, workflow_stage, updated_at} for the selected client, if one exists
  const [resumeBanner,   setResumeBanner]   = useState(true);   // dismissible — user can choose to start fresh instead
  const [resuming,       setResuming]       = useState(false);
  const [saveState,      setSaveState]      = useState("idle"); // idle | saving | saved | error — surfaced quietly, same spirit as AFS's autosave failure flagging

  // History
  const [history,        setHistory]        = useState([]);
  const [histLoading,    setHistLoading]    = useState(false);

  // Load firm + clients via API (same pattern as every other page)
  useEffect(() => {
    async function load() {
      if (!authFirm?.id) { navigate("/login"); return; }
      setLoading(true);
      setLoadError(null);
      try {
        const [firmResp, clientResp] = await Promise.all([
          getFirm(authFirm.id),
          getClients(authFirm.id),
        ]);
        setFirm(firmResp.data || authFirm);
        setClients(clientResp.data || []);
      } catch (e) {
        setLoadError("Failed to load clients. Please refresh the page.");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [authFirm, navigate]);

  // Load history when switching to history tab
  useEffect(() => {
    if (!selectedClient || activeTab !== "history") return;
    setHistLoading(true);
    getManagementAccountsHistory(selectedClient.id)
      .then(res => setHistory(res.jobs || []))
      .catch(() => setHistory([]))
      .finally(() => setHistLoading(false));
  }, [selectedClient, activeTab]);

  // Manual edits always win and are marked as such — a document upload can
  // pre-fill a field, but the preparer can always override it by hand.
  const setField      = useCallback((key, val) => {
    setFigures(p => ({...p,[key]:val}));
    setFigureSources(p => ({...p,[key]:"manual"}));
  }, []);
  const setPriorField = useCallback((key, val) => {
    setPriorFigures(p => ({...p,[key]:val}));
    setPriorFigureSources(p => ({...p,[key]:"manual"}));
  }, []);

  const totals      = computeTotals(figures);
  const priorTotals = usePrior ? computeTotals(priorFigures) : null;
  const hasAnyFigure = ALL_FIELDS.some(f => parseFloat(figures[f.key]) !== 0 && figures[f.key] !== "");

  // Step 2 validation
  const engagementValid = !!period && (externalUse ? !!preparedBy.trim() : true);

  async function handleSelectClient(c) {
    setSelectedClient(c); setStep(2); setResult(null);
    // Reset any previous client's draft state — a new client selection
    // starts clean until we know whether *this* client has a resumable pack.
    setMaJobId(null); setAvailableDraft(null); setResumeBanner(true);
    try {
      const { drafts } = await listMAJobDrafts(c.id);
      if (drafts && drafts.length > 0) setAvailableDraft(drafts[0]); // most recently updated first
    } catch {
      // Non-fatal — same spirit as the AFS duplicate-job check: if this
      // lookup fails, the preparer just doesn't see a resume prompt and
      // starts fresh, which is always a safe fallback.
    }
  }

  async function handleResumeDraft() {
    if (!availableDraft) return;
    setResuming(true);
    try {
      const draft = await resumeMAJobProgress(availableDraft.id);
      setMaJobId(draft.ma_job_id);
      setPeriod(draft.period || "");
      setPriorPeriod(draft.prior_period || PERIOD_OPTS[2]?.value || "");
      setUsePrior(!!draft.prior_period);
      setPreparedBy(draft.prepared_by || "");
      setExternalUse(!!draft.external_use);
      setFigureSources(draft.figure_sources || {});
      if (draft.figures && Object.keys(draft.figures).length) {
        setFigures(p => ({...p, ...Object.fromEntries(
          Object.entries(draft.figures).map(([k,v]) => [k, String(v)]))}));
      }
      if (draft.prior_figures && Object.keys(draft.prior_figures).length) {
        setPriorFigures(p => ({...p, ...Object.fromEntries(
          Object.entries(draft.prior_figures).map(([k,v]) => [k, String(v)]))}));
      }
      // Re-derive the source-documents panel from what's actually persisted
      // server-side — this is the whole point of persisting uploads at all,
      // rather than only restoring the figures they produced.
      try {
        const { documents } = await getMASourceDocuments(draft.ma_job_id);
        const restoredDocState = {};
        (documents || []).forEach(d => {
          restoredDocState[d.document_type] = {
            status: d.extraction_status === "failed" ? "error" : "done",
            fileName: d.file_name, error: d.extraction_error,
            notes: d.notes_verified || [],
          };
        });
        setDocState(restoredDocState);
      } catch { /* figures already restored above; document panel just stays empty — non-fatal */ }

      const stageToStep = { engagement:2, source_docs:3, figures:4, review:5 };
      setStep(stageToStep[draft.workflow_stage] || 2);
    } catch {
      setGenError("Could not restore the saved draft — starting fresh instead.");
    } finally {
      setResuming(false); setAvailableDraft(null);
    }
  }

  async function handleAdvanceToSourceDocs() {
    if (!engagementValid) return;
    let jobId = maJobId;
    if (!jobId) {
      try {
        const { ma_job_id } = await getOrCreateMAJob(selectedClient.id, period, selectedClient.entity_type);
        jobId = ma_job_id;
        setMaJobId(ma_job_id);
      } catch {
        // Non-fatal — draft creation failing must never block the actual
        // work. The preparer can still generate normally at the end; they
        // just lose resumability for this particular session.
      }
    }
    setStep(3);
  }

  // Continuous autosave (debounced) — mirrors the AFS bank-statement flow's
  // own autosave. Fires on every meaningful change once maJobId exists and
  // we're past the point of having something worth saving (step ≥ 3).
  // Deliberately does NOT fire once a pack has actually been generated
  // (`result` set) — a completed pack's canonical record is what generated
  // the PDF, matching the backend's own guard against overwriting it.
  useEffect(() => {
    if (!maJobId || step < 3 || result) return;
    setSaveState("saving");
    const stageForStep = { 3:"source_docs", 4:"figures", 5:"review" };
    const t = setTimeout(() => {
      saveMAJobProgress(maJobId, {
        stage: stageForStep[step] || "source_docs",
        prior_period: usePrior ? priorPeriod : null,
        figures: Object.fromEntries(Object.entries(figures).map(([k,v]) => [k, parseFloat(v) || 0])),
        prior_figures: usePrior
          ? Object.fromEntries(Object.entries(priorFigures).map(([k,v]) => [k, parseFloat(v) || 0]))
          : null,
        prepared_by: preparedBy.trim() || null,
        external_use: externalUse,
        figure_sources: { ...figureSources },
        source_documents: Object.entries(docState)
          .filter(([, s]) => s.status === "done").map(([type]) => DOC_TYPE_LABELS[type]),
      }).then(() => setSaveState("saved"))
        .catch(() => setSaveState("error")); // non-fatal, same as AFS — surfaced quietly, never blocks typing
    }, 1200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maJobId, step, figures, priorFigures, usePrior, priorPeriod, preparedBy, externalUse, figureSources, docState, result]);

  async function handleDocUpload(documentType, file) {
    setDocState(p => ({...p, [documentType]: { status:"uploading", fileName:file.name }}));
    try {
      const res = await extractManagementAccountsDocument(documentType, file, null, maJobId);
      const partial = res.figures_partial || {};

      if (documentType === "prior_year_afs") {
        setPriorFigures(p => ({...p, ...Object.fromEntries(
          Object.entries(partial).map(([k,v]) => [k, String(v)]))}));
        setPriorFigureSources(p => ({...p, ...Object.fromEntries(
          Object.keys(partial).map(k => [k, documentType]))}));
      } else {
        setFigures(p => ({...p, ...Object.fromEntries(
          Object.entries(partial).map(([k,v]) => [k, String(v)]))}));
        setFigureSources(p => ({...p, ...Object.fromEntries(
          Object.keys(partial).map(k => [k, documentType]))}));
      }

      setDocState(p => ({...p, [documentType]: {
        status:"done", fileName:file.name, notes: res.notes || [],
        needsReview: res.needs_review || [], transactionCount: res.transaction_count,
        accountCount: res.account_count, fieldsPopulated: Object.keys(partial),
      }}));
    } catch (err) {
      const msg = err?.response?.data?.detail;
      setDocState(p => ({...p, [documentType]: {
        status:"error", fileName:file.name,
        error: typeof msg === "string" ? msg : "Extraction failed — please try again or enter manually.",
      }}));
    }
  }

  function removeDoc(documentType) {
    setDocState(p => { const n = {...p}; delete n[documentType]; return n; });
    // Scan all sources rather than a fixed per-type key list — needed for
    // trial_balance, which can touch almost any current-period field
    // dynamically depending on what accounts the TB actually contains.
    if (documentType === "prior_year_afs") {
      setPriorFigureSources(p => {
        const n = {...p};
        Object.keys(n).forEach(k => { if (n[k] === documentType) delete n[k]; });
        return n;
      });
    } else {
      setFigureSources(p => {
        const n = {...p};
        Object.keys(n).forEach(k => { if (n[k] === documentType) delete n[k]; });
        return n;
      });
    }
  }

  const uploadedDocLabels = Object.entries(docState)
    .filter(([, s]) => s.status === "done")
    .map(([type]) => DOC_TYPE_LABELS[type]);

  async function handleGenerate() {
    setGenerating(true); setGenError(null); setResult(null);
    try {
      const parse = obj => Object.fromEntries(
        Object.entries(obj).map(([k,v]) => [k, parseFloat(v) || 0])
      );
      const payload = {
        client_id:     selectedClient.id,
        client_name:   selectedClient.name,
        entity_type:   selectedClient.entity_type || "",
        period,
        prior_period:  usePrior ? priorPeriod : null,
        figures:       parse(figures),
        prior_figures: usePrior ? parse(priorFigures) : null,
        firm_name:     firm?.name || "",
        firm_logo_b64: firm?.logo_b64 || null,
        prepared_by:   preparedBy.trim() || null,
        external_use:  externalUse,
        figure_sources: { ...figureSources },
        source_documents: uploadedDocLabels,
        ma_job_id:     maJobId,
      };
      const res = await generateManagementAccounts(payload);
      setResult(res);
    } catch (err) {
      const msg = err?.response?.data?.detail;
      if (typeof msg === "object" && msg?.code === "ADDON_REQUIRED") {
        setGenError("Management Accounts requires a plan upgrade. Contact support.");
      } else if (msg === "Management accounts PDF builder is not installed (backend/generate_management_accounts.py missing).") {
        setGenError("PDF builder is not yet deployed. Please contact support.");
      } else {
        setGenError(typeof msg === "string" ? msg : "Generation failed — please try again.");
      }
    } finally { setGenerating(false); }
  }

  function downloadFile(b64, filename, mimeType) {
    const bytes = Uint8Array.from(atob(b64), c => c.charCodeAt(0));
    const blob  = new Blob([bytes], { type: mimeType });
    const url   = URL.createObjectURL(blob);
    const a     = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  }
  const downloadPDF  = (b64, filename) => downloadFile(b64, filename, "application/pdf");
  const downloadDOCX = (b64, filename) => downloadFile(
    b64, filename, "application/vnd.openxmlformats-officedocument.wordprocessingml.document");

  async function handleRedownload(jobId, clientName, jobPeriod) {
    try {
      const res = await redownloadManagementAccounts(jobId);
      downloadPDF(res.pdf_b64,
        `Management-Accounts-${clientName.replace(/\s+/g,"-")}-${jobPeriod}.pdf`);
    } catch { alert("Failed to regenerate PDF. Please try again."); }
  }

  async function handleRedownloadWord(jobId, clientName, jobPeriod) {
    try {
      const res = await redownloadManagementAccountsWord(jobId);
      downloadDOCX(res.docx_b64,
        `Management-Accounts-${clientName.replace(/\s+/g,"-")}-${jobPeriod}.docx`);
    } catch { alert("Failed to regenerate Word document. Please try again."); }
  }

  function resetToNewPack() {
    setResult(null); setGenError(null);
    setFigures(EMPTY_FIGURES); setPriorFigures(EMPTY_FIGURES);
    setDocState({}); setFigureSources({}); setPriorFigureSources({});
    setStep(2);
  }

  // ── Render ───────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ minHeight:"100vh", background:T.bg, display:"flex",
        alignItems:"center", justifyContent:"center", fontFamily:"'Outfit',sans-serif" }}>
        <div style={{ textAlign:"center", color:T.mt }}>
          <Spinner />
          <p style={{ marginTop:"14px", fontSize:"14px" }}>Loading clients…</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div style={{ minHeight:"100vh", background:T.bg, display:"flex",
        alignItems:"center", justifyContent:"center", fontFamily:"'Outfit',sans-serif" }}>
        <div style={{ textAlign:"center", color:T.rd }}>
          <p style={{ fontSize:"15px" }}>{loadError}</p>
          <button onClick={() => window.location.reload()}
            style={{ marginTop:"12px", padding:"8px 20px", borderRadius:"8px",
              border:`1px solid ${T.rd}`, background:"transparent", color:T.rd,
              fontFamily:"'Outfit',sans-serif", cursor:"pointer", fontSize:"14px" }}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  const filteredClients = clients.filter(c =>
    c.name.toLowerCase().includes(clientSearch.toLowerCase())
  );

  const thS = { textAlign:"left", padding:"10px 16px", fontSize:"10px", fontWeight:700,
    color:T.lt, textTransform:"uppercase", letterSpacing:"0.08em" };
  const tdS = { padding:"10px 16px", fontSize:"14px" };

  return (
    <div style={{ minHeight:"100vh", background:T.bg,
      fontFamily:"'Outfit',sans-serif", color:T.tx }}>
      <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>

      {/* ── Header ── */}
      <div style={{ background:T.s1, borderBottom:`1px solid ${T.bd}`,
        padding:"14px 24px", position:"sticky", top:0, zIndex:10,
        display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <div>
          <h1 style={{ fontSize:"17px", fontWeight:700, color:T.tx, margin:0 }}>
            Management Accounts
          </h1>
          <p style={{ fontSize:"12px", color:T.mt, marginTop:"2px" }}>
            {selectedClient
              ? `${selectedClient.name} · ${selectedClient.entity_type || "Entity"}`
              : "Compile professional management accounts for your clients"}
          </p>
        </div>
        {selectedClient && (
          <div style={{ display:"flex", gap:"8px" }}>
            {[{key:"generate",label:"New Pack"},{key:"history",label:"History"}].map(tab => (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                style={{ padding:"7px 16px", borderRadius:"8px", border:"none",
                  fontFamily:"'Outfit',sans-serif", fontSize:"13px", fontWeight:600,
                  cursor:"pointer", transition:"all 0.15s",
                  background: activeTab===tab.key ? T.or : T.s3,
                  color: activeTab===tab.key ? T.onpri : T.mt }}>
                {tab.label}
              </button>
            ))}
          </div>
        )}
      </div>

      <div style={{ maxWidth:"820px", margin:"0 auto", padding:"32px 24px" }}>

        {/* ══ GENERATE FLOW ══ */}
        {activeTab === "generate" && (
          <>
            <StepBar current={step} />
            {maJobId && step >= 3 && !result && (
              <div style={{ display:"flex", justifyContent:"flex-end", marginTop:"-18px", marginBottom:"10px" }}>
                <span style={{ fontSize:"11px", color: saveState === "error" ? T.rd : T.lt,
                  display:"flex", alignItems:"center", gap:"5px" }}>
                  {saveState === "saving" && "Saving draft…"}
                  {saveState === "saved" && <><Icon name="check" /> Draft saved</>}
                  {saveState === "error" && <><Icon name="alert" /> Couldn't save draft — your work is still here, just not backed up</>}
                </span>
              </div>
            )}

            {/* ── STEP 1: Client ── */}
            {step === 1 && (
              <div style={card}>
                <h2 style={{ fontSize:"15px", fontWeight:700, margin:"0 0 4px" }}>Select a client</h2>
                <p style={{ fontSize:"13px", color:T.mt, margin:"0 0 18px" }}>
                  Choose the client you are preparing management accounts for.
                </p>

                {clients.length === 0 ? (
                  <div style={{ textAlign:"center", padding:"32px 0", color:T.mt }}>
                    <p style={{ fontSize:"14px" }}>No clients found.</p>
                    <button onClick={() => navigate("/clients")}
                      style={{ marginTop:"10px", padding:"8px 18px", borderRadius:"8px",
                        background:T.prg, border:"none", color:T.onpri, cursor:"pointer",
                        fontFamily:"'Outfit',sans-serif", fontWeight:600, fontSize:"13px" }}>
                      Add a client
                    </button>
                  </div>
                ) : (
                  <>
                    <input
                      type="text" placeholder="Search clients…"
                      value={clientSearch} onChange={e => setClientSearch(e.target.value)}
                      style={{ ...inp, width:"100%", boxSizing:"border-box", marginBottom:"14px" }} />
                    <div style={{ display:"flex", flexDirection:"column", gap:"8px",
                      maxHeight:"380px", overflowY:"auto" }}>
                      {filteredClients.map(c => (
                        <button key={c.id} onClick={() => handleSelectClient(c)}
                          style={{ display:"flex", alignItems:"center", justifyContent:"space-between",
                            padding:"12px 16px", borderRadius:"10px", cursor:"pointer",
                            border:`1px solid ${selectedClient?.id===c.id ? T.or : T.bd}`,
                            background: selectedClient?.id===c.id ? T.ord : T.s3,
                            color:T.tx, fontFamily:"'Outfit',sans-serif",
                            textAlign:"left", transition:"all 0.15s" }}>
                          <div>
                            <p style={{ margin:0, fontSize:"14px", fontWeight:600 }}>{c.name}</p>
                            <p style={{ margin:"2px 0 0", fontSize:"12px", color:T.mt }}>
                              {c.entity_type || "Entity"}
                              {c.registration_number ? ` · ${c.registration_number}` : ""}
                            </p>
                          </div>
                          <span style={{ fontSize:"18px", color:T.or, opacity:0.6 }}>›</span>
                        </button>
                      ))}
                      {filteredClients.length === 0 && (
                        <p style={{ fontSize:"14px", color:T.mt, textAlign:"center", padding:"20px 0" }}>
                          No clients match "{clientSearch}"
                        </p>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* ── STEP 2: Engagement details ── */}
            {step === 2 && selectedClient && (
              <div style={{ display:"flex", flexDirection:"column", gap:"16px" }}>
                {availableDraft && resumeBanner && (
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between",
                    gap:"14px", padding:"14px 18px", borderRadius:"12px",
                    background:T.bld, border:`1px solid ${alpha(T.bl, 0.31)}` }}>
                    <div>
                      <p style={{ margin:0, fontSize:"13px", fontWeight:700, color:T.tx }}>
                        Draft in progress for {selectedClient.name}
                      </p>
                      <p style={{ margin:"3px 0 0", fontSize:"12px", color:T.mt }}>
                        {availableDraft.period} · last saved{" "}
                        {availableDraft.updated_at
                          ? new Date(availableDraft.updated_at).toLocaleString()
                          : "earlier"}
                        {availableDraft.created_by_email ? ` by ${availableDraft.created_by_email}` : ""}
                      </p>
                    </div>
                    <div style={{ display:"flex", gap:"8px", flexShrink:0 }}>
                      <button onClick={() => setResumeBanner(false)}
                        style={{ padding:"8px 14px", borderRadius:"8px", border:`1px solid ${T.bd}`,
                          background:"transparent", color:T.mt, fontFamily:"'Outfit',sans-serif",
                          fontSize:"12px", fontWeight:600, cursor:"pointer" }}>
                        Start fresh
                      </button>
                      <button onClick={handleResumeDraft} disabled={resuming}
                        style={{ padding:"8px 16px", borderRadius:"8px", border:"none",
                          background:T.bl, color:T.onpri, fontFamily:"'Outfit',sans-serif",
                          fontSize:"12px", fontWeight:700, cursor: resuming ? "default" : "pointer",
                          display:"flex", alignItems:"center", gap:"6px" }}>
                        {resuming && <Spinner />} Continue draft →
                      </button>
                    </div>
                  </div>
                )}
                <div style={card}>
                  <h2 style={{ fontSize:"15px", fontWeight:700, margin:"0 0 4px" }}>
                    Engagement details
                  </h2>
                  <p style={{ fontSize:"13px", color:T.mt, margin:"0 0 20px" }}>
                    Set the reporting period and confirm the distribution basis before entering figures.
                  </p>

                  {/* Period selectors */}
                  <SectionLabel>Reporting Period</SectionLabel>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"16px" }}>
                    <div>
                      <label style={{ display:"block", fontSize:"11px", fontWeight:700, color:T.lt,
                        textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:"6px" }}>
                        Current period
                      </label>
                      <select value={period} onChange={e => setPeriod(e.target.value)}
                        style={{ ...inp, width:"100%", boxSizing:"border-box", cursor:"pointer" }}>
                        {PERIOD_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={{ display:"flex", alignItems:"center", gap:"7px",
                        fontSize:"11px", fontWeight:700, color:T.lt, textTransform:"uppercase",
                        letterSpacing:"0.06em", marginBottom:"6px", cursor:"pointer" }}>
                        <input type="checkbox" checked={usePrior}
                          onChange={e => setUsePrior(e.target.checked)}
                          style={{ accentColor:T.or, cursor:"pointer" }} />
                        Include prior period comparison
                      </label>
                      <select value={priorPeriod} onChange={e => setPriorPeriod(e.target.value)}
                        disabled={!usePrior}
                        style={{ ...inp, width:"100%", boxSizing:"border-box",
                          cursor: usePrior ? "pointer" : "not-allowed",
                          opacity: usePrior ? 1 : 0.35 }}>
                        {PERIOD_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </div>
                  </div>

                  {/* Preparer */}
                  <SectionLabel>Preparer</SectionLabel>
                  <div>
                    <label style={{ display:"block", fontSize:"11px", fontWeight:700, color:T.lt,
                      textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:"6px" }}>
                      Prepared by
                    </label>
                    <input type="text" value={preparedBy}
                      onChange={e => setPreparedBy(e.target.value)}
                      placeholder="e.g. Jane Smith CA(SA)"
                      style={{ ...inp, width:"320px", boxSizing:"border-box" }} />
                    <p style={{ fontSize:"12px", color:T.lt, margin:"5px 0 0" }}>
                      Required when issuing to an external party.
                    </p>
                  </div>

                  {/* Distribution basis */}
                  <SectionLabel>Distribution Basis</SectionLabel>
                  <div style={{ display:"flex", flexDirection:"column", gap:"10px" }}>
                    {[
                      {
                        value: false,
                        title: "Internal use only",
                        desc: "Draft figures for the client's own internal management review. Should not be distributed to third parties.",
                      },
                      {
                        value: true,
                        title: "External distribution",
                        desc: "Compilation engagement per ISRS 4410 (Revised). PDF will include a compilation statement and preparer sign-off, suitable for banks, landlords, investors, or other third parties.",
                      },
                    ].map(opt => (
                      <div key={String(opt.value)}
                        onClick={() => setExternalUse(opt.value)}
                        style={{ display:"flex", gap:"12px", alignItems:"flex-start",
                          padding:"14px 16px", borderRadius:"10px", cursor:"pointer",
                          border:`1px solid ${externalUse===opt.value ? T.or : T.bd}`,
                          background: externalUse===opt.value ? T.ord : T.s3,
                          transition:"all 0.15s" }}>
                        <div style={{ marginTop:"2px", width:"16px", height:"16px", flexShrink:0,
                          borderRadius:"50%", border:`2px solid ${externalUse===opt.value ? T.or : T.lt}`,
                          display:"flex", alignItems:"center", justifyContent:"center" }}>
                          {externalUse===opt.value && (
                            <div style={{ width:"8px", height:"8px", borderRadius:"50%",
                              background:T.or }} />
                          )}
                        </div>
                        <div>
                          <p style={{ margin:0, fontSize:"13px", fontWeight:700,
                            color: externalUse===opt.value ? T.tx : T.mt }}>{opt.title}</p>
                          <p style={{ margin:"3px 0 0", fontSize:"12px", color:T.mt,
                            lineHeight:"1.5" }}>{opt.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  {externalUse && !preparedBy.trim() && (
                    <div style={{ marginTop:"12px", padding:"10px 14px",
                      background:T.amd, border:`1px solid ${alpha(T.am, 0.31)}`,
                      borderRadius:"8px", fontSize:"13px", color:T.am }}>
                      <Icon name="alert" /> A preparer name is required for a valid ISRS 4410 compilation engagement.
                    </div>
                  )}
                </div>

                <div style={{ display:"flex", gap:"10px", justifyContent:"flex-end" }}>
                  <button onClick={() => setStep(1)}
                    style={{ padding:"10px 22px", borderRadius:"9px",
                      border:`1px solid ${T.bd}`, background:"transparent", color:T.mt,
                      fontFamily:"'Outfit',sans-serif", fontSize:"14px",
                      fontWeight:600, cursor:"pointer" }}>
                    ← Back
                  </button>
                  <button onClick={handleAdvanceToSourceDocs} disabled={!engagementValid}
                    style={{ padding:"10px 22px", borderRadius:"9px", border:"none",
                      background: engagementValid ? T.or : T.s3,
                      color: engagementValid ? T.onpri : T.mt,
                      fontFamily:"'Outfit',sans-serif", fontSize:"14px",
                      fontWeight:700, cursor: engagementValid ? "pointer" : "not-allowed",
                      transition:"all 0.15s" }}>
                    Continue to Source Documents →
                  </button>
                </div>
              </div>
            )}

            {/* ── STEP 3: Source Documents ── */}
            {step === 3 && selectedClient && (
              <div style={{ display:"flex", flexDirection:"column", gap:"16px" }}>
                <div style={card}>
                  <h2 style={{ fontSize:"15px", fontWeight:700, margin:"0 0 4px" }}>
                    Source Documents (optional)
                  </h2>
                  <p style={{ fontSize:"13px", color:T.mt, margin:"0 0 16px" }}>
                    Upload real documents to auto-fill figures instead of typing them by hand — every
                    figure below shows which document it came from. Nothing here is required; skip
                    straight to Figures if you'd rather enter everything manually.
                  </p>

                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"12px" }}>
                    {SOURCE_DOCUMENT_TYPES.map(doc => {
                      const s = docState[doc.type] || {};
                      return (
                        <div key={doc.type} style={{
                          padding:"14px", borderRadius:"10px",
                          border: doc.recommended ? `1.5px solid ${T.or}` : `1px solid ${T.bd}`,
                          background: s.status === "done" ? T.gnd
                                    : s.status === "error" ? T.rdd : T.s3 }}>
                          <div style={{ display:"flex", justifyContent:"space-between",
                            alignItems:"flex-start", gap:"8px" }}>
                            <p style={{ margin:0, fontSize:"13px", fontWeight:700, color:T.tx,
                              display:"flex", alignItems:"center", gap:"6px" }}>
                              {doc.title}
                              {doc.recommended && (
                                <span style={{ fontSize:"9px", fontWeight:700, padding:"2px 6px",
                                  borderRadius:"5px", background:T.or, color:T.onpri }}>
                                  RECOMMENDED
                                </span>
                              )}
                            </p>
                            {s.status === "done" && (
                              <span style={{ fontSize:"16px", color:T.gn, flexShrink:0 }}><Icon name="check" /></span>
                            )}
                          </div>
                          <p style={{ margin:"4px 0 6px", fontSize:"11px", color:T.mt }}>
                            Unlocks: {doc.unlocks}
                          </p>
                          <p style={{ margin:"0 0 10px", fontSize:"11.5px", color:T.lt, lineHeight:1.4 }}>
                            {doc.description}
                          </p>

                          {s.status === "uploading" && (
                            <div style={{ display:"flex", alignItems:"center", gap:"8px",
                              fontSize:"12px", color:T.or }}>
                              <Spinner /> Extracting from {s.fileName}…
                            </div>
                          )}

                          {s.status === "done" && (
                            <div>
                              <p style={{ margin:"0 0 4px", fontSize:"11.5px", color:T.gn, fontWeight:600 }}>
                                {s.fileName} — {s.fieldsPopulated?.length || 0} field(s) populated
                                {typeof s.transactionCount === "number" ? ` from ${s.transactionCount} transactions` : ""}
                                {typeof s.accountCount === "number" ? ` from ${s.accountCount} accounts` : ""}
                              </p>
                              {s.notes && s.notes.map((n, i) => (
                                <p key={i} style={{ margin:"2px 0", fontSize:"11px", color:T.lt, lineHeight:1.4 }}>
                                  ⓘ {n}
                                </p>
                              ))}
                              {s.needsReview && s.needsReview.length > 0 && (
                                <p style={{ margin:"4px 0 0", fontSize:"11px", color:T.am, fontWeight:600 }}>
                                  <Icon name="alert" /> {s.needsReview.length} {doc.type === "bank_statement" ? "transaction(s)" : "account(s)"} need
                                  manual review — not included in the auto-filled totals.
                                </p>
                              )}
                              <button onClick={() => removeDoc(doc.type)}
                                style={{ marginTop:"8px", padding:"4px 10px", borderRadius:"6px",
                                  border:`1px solid ${T.bd}`, background:"transparent", color:T.mt,
                                  fontSize:"11px", cursor:"pointer", fontFamily:"'Outfit',sans-serif" }}>
                                Remove & clear
                              </button>
                            </div>
                          )}

                          {s.status === "error" && (
                            <div>
                              <p style={{ margin:"0 0 8px", fontSize:"11.5px", color:T.rd }}>{s.error}</p>
                              <label style={{ display:"inline-block", padding:"6px 14px", borderRadius:"7px",
                                border:`1px solid ${T.bd}`, background:T.s1, color:T.tx,
                                fontSize:"12px", fontWeight:600, cursor:"pointer" }}>
                                Try again
                                <input type="file" accept={doc.accepts} style={{ display:"none" }}
                                  onChange={e => { if (e.target.files[0]) handleDocUpload(doc.type, e.target.files[0]); e.target.value=""; }} />
                              </label>
                            </div>
                          )}

                          {(!s.status || s.status === "idle") && (
                            <label style={{ display:"inline-block", padding:"6px 14px", borderRadius:"7px",
                              border:`1px solid ${T.or}`, background:T.orb, color:T.or,
                              fontSize:"12px", fontWeight:700, cursor:"pointer" }}>
                              Upload {doc.accepts.split(",")[0].replace(".","").toUpperCase()}
                              <input type="file" accept={doc.accepts} style={{ display:"none" }}
                                onChange={e => { if (e.target.files[0]) handleDocUpload(doc.type, e.target.files[0]); e.target.value=""; }} />
                            </label>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div style={{ display:"flex", gap:"10px", justifyContent:"flex-end" }}>
                  <button onClick={() => setStep(2)}
                    style={{ padding:"10px 22px", borderRadius:"9px",
                      border:`1px solid ${T.bd}`, background:"transparent", color:T.mt,
                      fontFamily:"'Outfit',sans-serif", fontSize:"14px",
                      fontWeight:600, cursor:"pointer" }}>
                    ← Back
                  </button>
                  <button onClick={() => setStep(4)}
                    style={{ padding:"10px 22px", borderRadius:"9px", border:"none",
                      background: T.prg, color:T.onpri,
                      fontFamily:"'Outfit',sans-serif", fontSize:"14px",
                      fontWeight:700, cursor:"pointer", transition:"all 0.15s" }}>
                    Continue to Figures →
                  </button>
                </div>
              </div>
            )}

            {/* ── STEP 4: Figures ── */}
            {step === 4 && selectedClient && (
              <div style={{ display:"flex", flexDirection:"column", gap:"16px" }}>

                {/* IS */}
                <div style={card}>
                  <h2 style={{ fontSize:"15px", fontWeight:700, margin:"0 0 4px" }}>Income Statement</h2>
                  <p style={{ fontSize:"13px", color:T.mt, margin:"0 0 4px" }}>
                    {periodLabel(period)}{usePrior ? ` vs ${periodLabel(priorPeriod)}` : ""}
                  </p>

                  <div style={{ display:"grid",
                    gridTemplateColumns: usePrior ? "1fr 1fr" : "1fr", gap:"0 32px" }}>
                    <div>
                      {usePrior && <SectionLabel>Current — {periodLabel(period)}</SectionLabel>}
                      {IS_FIELDS.map(f => (
                        <FigureRow key={f.key} label={f.label}
                          value={figures[f.key]} onChange={v => setField(f.key, v)}
                          source={figureSources[f.key]} />
                      ))}
                      <FigureRow label="Gross Profit" value={fmt(totals.gp)} highlight />
                      <FigureRow label="Net Profit / (Loss)" value={fmt(totals.net)} highlight />
                    </div>
                    {usePrior && (
                      <div>
                        <SectionLabel>Prior — {periodLabel(priorPeriod)}</SectionLabel>
                        {IS_FIELDS.map(f => (
                          <FigureRow key={f.key} label={f.label}
                            value={priorFigures[f.key]} onChange={v => setPriorField(f.key, v)}
                            source={priorFigureSources[f.key]} />
                        ))}
                        <FigureRow label="Gross Profit" value={fmt(priorTotals?.gp)} highlight />
                        <FigureRow label="Net Profit / (Loss)" value={fmt(priorTotals?.net)} highlight />
                      </div>
                    )}
                  </div>
                </div>

                {/* BS */}
                <div style={card}>
                  <h2 style={{ fontSize:"15px", fontWeight:700, margin:"0 0 4px" }}>
                    Statement of Financial Position
                  </h2>
                  <p style={{ fontSize:"13px", color:T.mt, margin:"0 0 4px" }}>
                    Assets must equal Liabilities + Equity.
                  </p>

                  {totals.bsDiff > 1 && hasAnyFigure && (
                    <div style={{ margin:"10px 0", padding:"10px 14px",
                      background:T.amd, border:`1px solid ${alpha(T.am, 0.31)}`,
                      borderRadius:"8px", fontSize:"13px", color:T.am }}>
                      <Icon name="alert" /> Balance sheet is out by{" "}
                      {totals.bsDiff.toLocaleString("en-ZA", {maximumFractionDigits:0})} —
                      review your figures before generating.
                    </div>
                  )}

                  <div style={{ display:"grid",
                    gridTemplateColumns: usePrior ? "1fr 1fr" : "1fr", gap:"0 32px" }}>
                    <div>
                      {usePrior && <SectionLabel>Current — {periodLabel(period)}</SectionLabel>}
                      <SectionLabel>Assets</SectionLabel>
                      {BS_ASSET_FIELDS.map(f => (
                        <FigureRow key={f.key} label={f.label}
                          value={figures[f.key]} onChange={v => setField(f.key, v)}
                          source={figureSources[f.key]} />
                      ))}
                      <FigureRow label="Total Assets" value={fmt(totals.assets)} highlight />
                      <SectionLabel>Liabilities & Equity</SectionLabel>
                      {BS_LE_FIELDS.map(f => (
                        <FigureRow key={f.key} label={f.label}
                          value={figures[f.key]} onChange={v => setField(f.key, v)}
                          source={figureSources[f.key]} />
                      ))}
                      <FigureRow label="Total Liabilities & Equity"
                        value={fmt(totals.liab + totals.eq)} highlight />
                    </div>
                    {usePrior && (
                      <div>
                        <SectionLabel>Prior — {periodLabel(priorPeriod)}</SectionLabel>
                        <SectionLabel>Assets</SectionLabel>
                        {BS_ASSET_FIELDS.map(f => (
                          <FigureRow key={f.key} label={f.label}
                            value={priorFigures[f.key]} onChange={v => setPriorField(f.key, v)}
                            source={priorFigureSources[f.key]} />
                        ))}
                        <FigureRow label="Total Assets"
                          value={fmt(priorTotals?.assets)} highlight />
                        <SectionLabel>Liabilities & Equity</SectionLabel>
                        {BS_LE_FIELDS.map(f => (
                          <FigureRow key={f.key} label={f.label}
                            value={priorFigures[f.key]} onChange={v => setPriorField(f.key, v)}
                            source={priorFigureSources[f.key]} />
                        ))}
                        <FigureRow label="Total Liabilities & Equity"
                          value={fmt((priorTotals?.liab||0)+(priorTotals?.eq||0))} highlight />
                      </div>
                    )}
                  </div>
                </div>

                <div style={{ display:"flex", gap:"10px", justifyContent:"flex-end" }}>
                  <button onClick={() => setStep(3)}
                    style={{ padding:"10px 22px", borderRadius:"9px",
                      border:`1px solid ${T.bd}`, background:"transparent", color:T.mt,
                      fontFamily:"'Outfit',sans-serif", fontSize:"14px",
                      fontWeight:600, cursor:"pointer" }}>
                    ← Back
                  </button>
                  <button onClick={() => setStep(5)} disabled={!hasAnyFigure}
                    style={{ padding:"10px 22px", borderRadius:"9px", border:"none",
                      background: hasAnyFigure ? T.or : T.s3,
                      color: hasAnyFigure ? T.onpri : T.mt,
                      fontFamily:"'Outfit',sans-serif", fontSize:"14px",
                      fontWeight:700, cursor: hasAnyFigure ? "pointer" : "not-allowed",
                      transition:"all 0.15s" }}>
                    Review & Generate →
                  </button>
                </div>
              </div>
            )}

            {/* ── STEP 5: Review & Generate ── */}
            {step === 5 && selectedClient && (
              <div style={{ display:"flex", flexDirection:"column", gap:"16px" }}>

                {/* Summary card */}
                <div style={card}>
                  <h2 style={{ fontSize:"15px", fontWeight:700, margin:"0 0 16px" }}>
                    Review before generating
                  </h2>

                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"12px",
                    marginBottom:"20px" }}>
                    {[
                      ["Client",  selectedClient.name],
                      ["Entity type", selectedClient.entity_type || "—"],
                      ["Period", periodLabel(period)],
                      ["Comparison", usePrior ? periodLabel(priorPeriod) : "None"],
                      ["Prepared by", preparedBy.trim() || "Not specified"],
                      ["Distribution", externalUse ? "External (ISRS 4410)" : "Internal only"],
                      ["Source documents", uploadedDocLabels.length ? uploadedDocLabels.join(", ") : "None — figures entered manually"],
                    ].map(([label, value]) => (
                      <div key={label} style={{ padding:"10px 14px", background:T.s3,
                        borderRadius:"8px", border:`1px solid ${T.bd}` }}>
                        <p style={{ margin:0, fontSize:"10px", fontWeight:700, color:T.lt,
                          textTransform:"uppercase", letterSpacing:"0.07em" }}>{label}</p>
                        <p style={{ margin:"3px 0 0", fontSize:"13px", fontWeight:600,
                          color: label==="Distribution" && externalUse ? T.gn : T.tx }}>
                          {value}
                        </p>
                      </div>
                    ))}
                  </div>

                  {/* Key figures summary */}
                  <div style={{ borderTop:`1px solid ${T.bd}`, paddingTop:"16px", marginBottom:"16px" }}>
                    <p style={{ margin:"0 0 10px", fontSize:"11px", fontWeight:700, color:T.lt,
                      textTransform:"uppercase", letterSpacing:"0.08em" }}>Key figures</p>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:"10px" }}>
                      {[
                        ["Revenue",           fmt(totals.rev)],
                        ["Gross profit",      fmt(totals.gp)],
                        ["Net profit/(loss)", fmt(totals.net)],
                        ["Total assets",      fmt(totals.assets)],
                        ["Total liabilities", fmt(totals.liab)],
                        ["Equity",            fmt(totals.eq)],
                      ].map(([label, value]) => (
                        <div key={label} style={{ padding:"8px 12px", background:T.s3,
                          borderRadius:"8px", border:`1px solid ${T.bd}` }}>
                          <p style={{ margin:0, fontSize:"11px", color:T.lt }}>{label}</p>
                          <p style={{ margin:"2px 0 0", fontSize:"13px", fontWeight:700,
                            color: parseFloat(value) < 0 ? T.rd : T.tx }}>{value}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {totals.bsDiff > 1 && (
                    <div style={{ marginBottom:"16px", padding:"10px 14px",
                      background:T.amd, border:`1px solid ${alpha(T.am, 0.31)}`,
                      borderRadius:"8px", fontSize:"13px", color:T.am }}>
                      <Icon name="alert" /> Balance sheet is out by{" "}
                      {totals.bsDiff.toLocaleString("en-ZA",{maximumFractionDigits:0})}.
                      The PDF will include a warning. You may still proceed.
                    </div>
                  )}

                  {genError && (
                    <div style={{ marginBottom:"16px", padding:"10px 14px",
                      background:T.rdd, border:`1px solid ${alpha(T.rd, 0.31)}`,
                      borderRadius:"8px", fontSize:"13px", color:T.rd,
                      display:"flex", alignItems:"center", justifyContent:"space-between",
                      gap:"12px", flexWrap:"wrap" }}>
                      <span>{genError}</span>
                      {/* Unpaid-account lock (see is_generation_locked in main.py) surfaces
                          here as a plain 402 detail string — same pattern as the AFS flow
                          in Statements.jsx: matched on wording since no structured error
                          code exists yet. Fragile but scoped: only adds a shortcut button,
                          never blocks anything on its own. */}
                      {pointsToBilling(genError) && (
                        <button onClick={() => navigate('/billing')}
                          style={{ padding:'6px 14px', borderRadius:'6px', border:`1px solid ${T.rd}`,
                            background:'transparent', color:T.rd, fontSize:'12px', fontWeight:'700',
                            cursor:'pointer', whiteSpace:'nowrap' }}>
                          Go to Billing →
                        </button>
                      )}
                    </div>
                  )}

                  {!result ? (
                    <button onClick={handleGenerate} disabled={generating}
                      style={{ width:"100%", padding:"14px", borderRadius:"10px", border:"none",
                        background: generating ? T.s3 : T.prg,
                        color: generating ? T.mt : T.onpri,
                        fontFamily:"'Outfit',sans-serif", fontSize:"15px", fontWeight:700,
                        cursor: generating ? "not-allowed" : "pointer",
                        display:"flex", alignItems:"center", justifyContent:"center", gap:"10px",
                        transition:"all 0.2s" }}>
                      {generating ? <><Spinner /> Generating — this may take a moment…</> : "Generate Management Accounts"}
                    </button>
                  ) : (
                    <div>
                      <div style={{ padding:"16px", background:T.gnd,
                        border:`1px solid ${alpha(T.gn, 0.25)}`, borderRadius:"10px",
                        display:"flex", alignItems:"center", justifyContent:"space-between",
                        marginBottom:"14px" }}>
                        <div style={{ display:"flex", alignItems:"center", gap:"10px" }}>
                          <span style={{ fontSize:"22px" }}><Icon name="check" /></span>
                          <div>
                            <p style={{ margin:0, fontSize:"14px", fontWeight:700, color:T.gn }}>
                              Management accounts ready
                            </p>
                            <p style={{ margin:"2px 0 0", fontSize:"12px", color:T.mt }}>
                              {externalUse ? "Compilation engagement (ISRS 4410 (Revised))" : "Internal use only"}
                            </p>
                          </div>
                        </div>
                        <div style={{ display:"flex", gap:"8px" }}>
                          <button
                            onClick={() => downloadPDF(result.pdf_b64,
                              `Management-Accounts-${selectedClient.name.replace(/\s+/g,"-")}-${period}.pdf`)}
                            style={{ padding:"10px 22px", borderRadius:"9px", border:"none",
                              background:T.gn, color:T.onpri, fontFamily:"'Outfit',sans-serif",
                              fontSize:"14px", fontWeight:700, cursor:"pointer" }}>
                            ↓ Download PDF
                          </button>
                          {result.docx_b64 && (
                            <button
                              onClick={() => downloadDOCX(result.docx_b64,
                                `Management-Accounts-${selectedClient.name.replace(/\s+/g,"-")}-${period}.docx`)}
                              style={{ padding:"10px 22px", borderRadius:"9px",
                                border:`1px solid ${T.gn}`, background:"transparent", color:T.gn,
                                fontFamily:"'Outfit',sans-serif",
                                fontSize:"14px", fontWeight:700, cursor:"pointer" }}>
                              ↓ Download Word
                            </button>
                          )}
                        </div>
                      </div>

                      {result.ai_commentary && (
                        <div style={{ ...card, border:`1px solid ${T.bd}`, background:T.s3,
                          marginBottom:"14px" }}>
                          <p style={{ margin:"0 0 10px", fontSize:"11px", fontWeight:700, color:T.lt,
                            textTransform:"uppercase", letterSpacing:"0.08em" }}>
                            AI Commentary Preview
                          </p>
                          <div style={{ fontSize:"13px", color:T.mt, lineHeight:"1.8",
                            display:"flex", flexDirection:"column", gap:"10px" }}>
                            {result.ai_commentary.split("\n\n").filter(Boolean).map((p, i) => (
                              <p key={i} style={{ margin:0 }}>{p}</p>
                            ))}
                          </div>
                        </div>
                      )}

                      <button onClick={resetToNewPack}
                        style={{ width:"100%", padding:"11px", borderRadius:"9px",
                          border:`1px solid ${T.bd}`, background:"transparent", color:T.mt,
                          fontFamily:"'Outfit',sans-serif", fontSize:"14px",
                          fontWeight:600, cursor:"pointer" }}>
                        Prepare another pack for this client
                      </button>
                    </div>
                  )}
                </div>

                {!result && (
                  <div style={{ display:"flex", gap:"10px", justifyContent:"flex-start" }}>
                    <button onClick={() => setStep(4)}
                      style={{ padding:"10px 22px", borderRadius:"9px",
                        border:`1px solid ${T.bd}`, background:"transparent", color:T.mt,
                        fontFamily:"'Outfit',sans-serif", fontSize:"14px",
                        fontWeight:600, cursor:"pointer" }}>
                      ← Edit figures
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* ══ HISTORY TAB ══ */}
        {activeTab === "history" && selectedClient && (
          <div style={{ ...card, padding:0, overflow:"hidden" }}>
            {histLoading ? (
              <div style={{ padding:"48px", textAlign:"center", color:T.mt }}>
                <Spinner />
                <p style={{ marginTop:"12px", fontSize:"14px" }}>Loading history…</p>
              </div>
            ) : history.length === 0 ? (
              <div style={{ padding:"48px", textAlign:"center" }}>
                <p style={{ fontSize:"14px", color:T.mt }}>
                  No management accounts have been generated for this client yet.
                </p>
                <button onClick={() => setActiveTab("generate")}
                  style={{ marginTop:"12px", padding:"9px 20px", borderRadius:"8px",
                    background:T.prg, border:"none", color:T.onpri, cursor:"pointer",
                    fontFamily:"'Outfit',sans-serif", fontWeight:600, fontSize:"13px" }}>
                  Generate first pack
                </button>
              </div>
            ) : (
              <table style={{ width:"100%", borderCollapse:"collapse" }}>
                <thead style={{ background:T.s3 }}>
                  <tr>
                    <th style={thS}>Period</th>
                    <th style={thS}>Comparison</th>
                    <th style={thS}>Basis</th>
                    <th style={thS}>Generated</th>
                    <th style={{ ...thS, textAlign:"right" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {history.map(job => (
                    <tr key={job.id} style={{ borderTop:`1px solid ${T.bd}` }}>
                      <td style={{ ...tdS, fontWeight:600 }}>{periodLabel(job.period)}</td>
                      <td style={{ ...tdS, color:T.mt }}>
                        {job.prior_period ? periodLabel(job.prior_period) : "—"}
                      </td>
                      <td style={{ ...tdS }}>
                        <span style={{ fontSize:"12px", fontWeight:600, padding:"3px 8px",
                          borderRadius:"6px",
                          background: job.external_use ? T.gnd : T.amd,
                          color: job.external_use ? T.gn : T.am }}>
                          {job.external_use ? "External (ISRS 4410)" : "Internal"}
                        </span>
                      </td>
                      <td style={{ ...tdS, color:T.mt }}>
                        {new Date(job.created_at).toLocaleDateString("en-ZA",{
                          day:"numeric", month:"short", year:"numeric",
                        })}
                      </td>
                      <td style={{ ...tdS, textAlign:"right" }}>
                        <button
                          onClick={() => handleRedownload(job.id, selectedClient.name, job.period)}
                          style={{ fontSize:"13px", color:T.or, background:"none", border:"none",
                            cursor:"pointer", fontFamily:"'Outfit',sans-serif",
                            textDecoration:"underline", marginRight:"12px" }}>
                          PDF
                        </button>
                        <button
                          onClick={() => handleRedownloadWord(job.id, selectedClient.name, job.period)}
                          style={{ fontSize:"13px", color:T.or, background:"none", border:"none",
                            cursor:"pointer", fontFamily:"'Outfit',sans-serif",
                            textDecoration:"underline" }}>
                          Word
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Prompt to select client if on history with no client */}
        {activeTab === "history" && !selectedClient && (
          <div style={{ ...card, textAlign:"center", padding:"48px" }}>
            <p style={{ fontSize:"14px", color:T.mt }}>
              Select a client from the Generate tab to view their history.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
